"use client";

import Box from "@mui/material/Box";
import { motion } from "motion/react";
import { useEntranceStill } from "@/components/motion/useEntranceStill";
import { BrowserMock } from "./BrowserMock";
import { PhoneMock } from "./PhoneMock";

/** Entrance spring shared by both devices — settles fast, no oscillating tail. */
const settleSpring = { type: "spring", stiffness: 120, damping: 18 } as const;

/** The stage: the shot bleeds slightly past its grid column on desktop. */
const stageSx = {
  position: "relative",
  width: { xs: "100%", md: "116%" },
} as const;

/** Phone placement, overlapping the browser's lower-left corner. */
const phoneSx = {
  position: "absolute",
  left: { xs: "0%", md: "-4%" },
  bottom: { xs: "-12%", md: "-18%" },
  width: { xs: "30%", md: "33%" },
} as const;

/**
 * The hero product shot: a browser dashboard with an iPhone overlapping its
 * lower-left corner — the "web and iOS" story made literal. Both are pure
 * DOM/CSS so they stay crisp and on-theme.
 *
 * Motion is entrance-only: the browser settles into place with a slight
 * perspective tip on load, the phone follows a beat later, and then both hold
 * completely still — no pointer tracking, no idle loops. While the entrance
 * gate is closed (SSR, hydration, reduced motion) the resting layout renders
 * with no motion elements, per the useEntranceStill contract.
 */
export function HeroDevices() {
  const still = useEntranceStill();

  if (still) {
    return (
      <Box aria-hidden sx={stageSx}>
        <BrowserMock />
        <Box sx={phoneSx}>
          <PhoneMock />
        </Box>
      </Box>
    );
  }

  return (
    <Box aria-hidden sx={stageSx}>
      {/* Browser settles from a slight perspective tip to flat. */}
      <motion.div
        initial={{ opacity: 0, y: 28, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ ...settleSpring, delay: 0.1 }}
        style={{ transformPerspective: 1200 }}
      >
        <BrowserMock />
      </motion.div>
      {/* Phone lands on a slightly later beat, overlapping the corner. */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 36, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ ...settleSpring, delay: 0.28 }}
        sx={phoneSx}
      >
        <PhoneMock />
      </Box>
    </Box>
  );
}
